import { T, Version } from "@utils/enums";
import React, { createContext, useState, useContext, useEffect } from 'react';
import { getUrlParam } from "@utils/functions";
import { UrlParams } from "@utils/enums";
import { useVersion } from "@utils/hooks/useVersion";   

interface ThemeContextType {
    theme: T;
    isDynamicColors: boolean;
    version: Version;
    toggleTheme: (themeURL?: string) => void;
    updateDynamicColors: (isDynamicColors: boolean) => void;
    updateVersion: (version: Version) => void;
}

const ThemeContext = createContext<ThemeContextType>({ 
    theme: T.LIGHT, 
    isDynamicColors: true,
    version: Version.V2,
    toggleTheme: () => {},
    updateDynamicColors: () => {},
    updateVersion: () => {}
});

// Function to get values from URL on start
const getInitialTheme = (): T => {
    const themeURL = getUrlParam(UrlParams.THEME);
    if (themeURL && (themeURL === 'light' || themeURL === 'dark')) {
        return themeURL as T;
    }
    return T.LIGHT;
};

const getInitialDynamicColors = (): boolean => {
    const dynamicColorsURL = getUrlParam(UrlParams.DYNAMIC_COLORS);
    if (dynamicColorsURL) {
        return dynamicColorsURL === 'true';
    }
    return true;
};

const getInitialVersion = (): Version => {
    const versionURL = getUrlParam(UrlParams.VERSION);
    if (versionURL && Object.values(Version).includes(versionURL as Version)) {
        return versionURL as Version;
    }
    return Version.V2;
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    // Initialize with values from URL immediately
    const [theme, setTheme] = useState<T>(getInitialTheme);
    const [isDynamicColors, setDynamicColors] = useState<boolean>(getInitialDynamicColors);
    const [version, setVersion] = useState<Version>(getInitialVersion);
    const { isV2 } = useVersion();
    // Automatically set isDynamicColors to true when version is V2
    useEffect(() => {
        if (isV2) {
            setDynamicColors(true);
        }
    }, [version]);

    const toggleTheme = (themeName?: string) => {
        setTheme((prevTheme) => {
          if (themeName) {
            return themeName as T;
          } else {
            const newTheme = prevTheme === T.LIGHT ? T.DARK : T.LIGHT;
            return newTheme;
          }
        });
      };

    const updateDynamicColors = (value: boolean) => {
        // If version is V2, always keep isDynamicColors as true
        if (isV2) {
            setDynamicColors(true);
        } else {
            setDynamicColors(value);
        }
    };

    const updateVersion = (newVersion: Version) => {
        setVersion(newVersion);
        // Automatically set isDynamicColors to true when switching to V2
        if (newVersion === Version.V2) {
            setDynamicColors(true);
        }
    };

    return (
        <ThemeContext.Provider value={{ theme, isDynamicColors, version, toggleTheme, updateDynamicColors, updateVersion }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    return useContext(ThemeContext);
};